import type { ClientPlanView, PaymentSession, Plan, SavedCardView, SellMethod } from "@vaulted/shared";
import { PLAN_STATUS_LABELS, describeBenefit, formatCents } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../api";
import { useCatalog } from "../pages/Catalog";
import { priceLabel, useNameOf, usePlans } from "../pages/Plans";
import { useSession } from "../session";
import { AgreementText } from "./Agreement";
import { StripeCheckout } from "./StripeCheckout";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, MoneyField, Panel, PanelHeader, SelectField, formatDateTime } from "./ui";

type SellResult = { plans: ClientPlanView[]; session: PaymentSession | null; message: string | null; changeCents: number | null };

const fmtDate = (d: string | null) => (d ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${d.slice(0, 10)}T12:00:00Z`)) : "—");
const fmtInstantDate = (iso: string) => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso));
const statusTone = (s: ClientPlanView["status"]) => (s === "active" ? "ok" : s === "on_hold" || s === "pending" ? "wait" : "off");

/** Client profile → Plans: memberships and packages, their vouchers, and billing. */
export function ClientPlansTab({ clientId, clientName }: { clientId: string; clientName: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ["client-plans", clientId], queryFn: () => get<ClientPlanView[]>(`/api/clients/${clientId}/plans`) });
  const [selling, setSelling] = useState(false);
  const [bringing, setBringing] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["client-plans", clientId] });
    void qc.invalidateQueries({ queryKey: ["client-payments", clientId] });
    void qc.invalidateQueries({ queryKey: ["client-cards", clientId] });
  };
  if (plans.isLoading) return <Loading />;
  if (plans.error || !plans.data) return <Alert>{errorMessage(plans.error)}</Alert>;
  const current = plans.data.filter((p) => p.status !== "ended");
  const ended = plans.data.filter((p) => p.status === "ended");

  return (
    <div className="flex flex-col gap-4">
      {(can("plans.sell") || can("plans.manage_client")) && (
        <div className="flex flex-wrap gap-2">
          {can("plans.sell") && (
            <Button variant="primary" onClick={() => setSelling(true)}>
              Sell a membership or package
            </Button>
          )}
          {can("plans.manage_client") && <Button onClick={() => setBringing(true)}>Bring over a membership (already paid)</Button>}
        </div>
      )}
      {current.length === 0 && <Alert tone="info">No memberships or packages.</Alert>}
      {current.map((p) => (
        <PlanCard key={p.id} p={p} clientId={clientId} onChanged={refresh} />
      ))}
      {ended.length > 0 && (
        <button className="self-start text-sm font-medium text-muted hover:text-ink" onClick={() => setShowEnded((v) => !v)}>
          {showEnded ? "Hide ended" : `Show ended (${ended.length})`}
        </button>
      )}
      {showEnded && ended.map((p) => <PlanCard key={p.id} p={p} clientId={clientId} onChanged={refresh} />)}
      {bringing && <BringOverDialog clientId={clientId} clientName={clientName} onClose={() => (setBringing(false), refresh())} />}
      {selling && <SellDialog clientId={clientId} clientName={clientName} onClose={() => (setSelling(false), refresh())} />}
    </div>
  );
}

function PlanCard({ p, clientId, onChanged }: { p: ClientPlanView; clientId: string; onChanged: () => void }) {
  const { can } = useSession();
  const restore = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => post(`/api/vouchers/${id}/restore`, { reason }),
    onSuccess: onChanged,
  });
  const courtesy = useMutation({ mutationFn: (reason: string) => post(`/api/client-plans/${p.id}/vouchers`, { reason }), onSuccess: onChanged });
  const removeVoucher = useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string }) => post(`/api/vouchers/${id}/remove`, { reason }), onSuccess: onChanged });
  const plans = usePlans();
  const hasVouchers = p.vouchers.length > 0 || !!plans.data?.find((x) => x.id === p.planId)?.benefits.some((b) => b.kind === "voucher");
  const [agreement, setAgreement] = useState(false);
  const [allVouchers, setAllVouchers] = useState(false);
  const abandon = useMutation({ mutationFn: () => post(`/api/client-plans/${p.id}/abandon`), onSuccess: onChanged });
  const usable = p.vouchers.filter((v) => v.status === "available" || v.status === "held");
  const shown = allVouchers ? p.vouchers : usable;
  return (
    <Panel>
      <PanelHeader
        title={p.name}
        description={`${priceLabel(p)} · since ${fmtDate(p.startedOn)}${p.soldBy ? ` · sold by ${p.soldBy}` : ""}`}
        action={<Badge tone={statusTone(p.status)}>{p.status === "ended" && p.endReason ? `Ended: ${p.endReason}` : PLAN_STATUS_LABELS[p.status]}</Badge>}
      />
      <div className="flex flex-col gap-4 px-5 pb-5">
        {p.status === "pending" && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="flex-1 text-sm text-ink-soft">The payment hasn't gone through yet. If the client didn't finish paying, drop it and sell it again.</p>
            {can("plans.sell") && (
              <Button variant="quiet" busy={abandon.isPending} onClick={() => abandon.mutate()}>
                Drop it
              </Button>
            )}
          </div>
        )}
        {p.kind === "membership" && p.status !== "ended" && (
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
            <Fact label="Next billing" value={fmtDate(p.nextBillingOn)} />
            <Fact label="Minimum until" value={fmtDate(p.commitmentEndsOn)} />
            <Fact label="Card" value={p.card ? `${p.card.brand.toUpperCase()} •••• ${p.card.last4}` : "None: add one before the next billing"} />
            {p.cancelEffectiveOn && <Fact label="Ends" value={fmtDate(p.cancelEffectiveOn)} />}
          </dl>
        )}
        {p.kind === "membership" && (p.status === "active" || p.status === "on_hold") && <MembershipActions p={p} clientId={clientId} onChanged={onChanged} />}
        <div>
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-sm font-semibold">
              Vouchers: {p.vouchersLeft} left{p.status === "on_hold" ? " (on hold until paid)" : ""}
            </h3>
            {p.vouchers.length > usable.length && (
              <button className="text-sm text-muted hover:text-ink" onClick={() => setAllVouchers((v) => !v)}>
                {allVouchers ? "Only unused" : `Show all ${p.vouchers.length}`}
              </button>
            )}
          </div>
          {shown.length > 0 && (
            <ul className="mt-1 text-sm">
              {shown.map((v) => (
                <li key={v.id} className="flex flex-wrap gap-x-3 border-b border-mist py-1.5 last:border-0">
                  <span className="flex-1">{v.label}</span>
                  {can("plans.manage_client") && v.status === "available" && (
                    <button
                      className="text-xs text-muted hover:text-danger"
                      onClick={() => {
                        const reason = prompt(`Remove this ${v.label} voucher? Reason:`);
                        if (reason) removeVoucher.mutate({ id: v.id, reason });
                      }}
                    >
                      Remove
                    </button>
                  )}
                  {can("plans.manage_client") && (v.status === "used" || v.status === "expired") && p.status !== "ended" && (
                    <button
                      className="text-xs text-brand hover:underline"
                      onClick={() => {
                        const reason = prompt("Why give this voucher back?");
                        if (reason) restore.mutate({ id: v.id, reason });
                      }}
                    >
                      Give back
                    </button>
                  )}
                  <span className="text-muted">
                    {v.status === "used" && v.visit
                      ? `Used ${fmtDate(v.visit.date)} (${v.visit.serviceName})`
                      : v.status === "held" && v.visit
                        ? `Held for ${fmtDate(v.visit.date)}`
                        : v.status === "available"
                          ? v.expiresAt
                            ? `Expires ${fmtInstantDate(v.expiresAt)}`
                            : "Never expires"
                          : v.status === "expired"
                            ? `Expired ${v.expiresAt ? fmtInstantDate(v.expiresAt) : ""}`
                            : v.status === "forfeited"
                              ? (v.note ?? "Forfeited")
                              : v.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {p.charges.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold">Billing</h3>
            <ul className="mt-1 text-sm">
              {p.charges.map((c) => (
                <li key={c.id} className="flex flex-wrap gap-x-3 border-b border-mist py-1.5 last:border-0">
                  <span className="flex-1">{p.kind === "membership" ? `${fmtDate(c.periodStart)} – ${fmtDate(c.periodEnd)}` : fmtDate(c.periodStart)}</span>
                  {c.failureMessage && <span className="text-danger">{c.failureMessage}</span>}
                  <Badge tone={c.status === "paid" ? "ok" : c.status === "failed" ? "wait" : "off"}>{c.status === "paid" ? "Paid" : c.status === "pending" ? "Waiting" : c.status === "failed" ? "Failed" : "Void"}</Badge>
                  <span className="w-20 text-right tabular-nums">{formatCents(c.amountCents)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {can("plans.manage_client") && (p.status === "active" || p.status === "on_hold") && hasVouchers && (
          <button
            className="self-start text-sm font-semibold text-muted hover:text-ink"
            onClick={() => {
              const reason = prompt("Add a voucher (one at a time). Reason (e.g. left over from Boulevard, or a courtesy):");
              if (reason) courtesy.mutate(reason);
            }}
          >
            + Add a voucher
          </button>
        )}
        {(restore.error || courtesy.error || removeVoucher.error) && <Alert>{errorMessage(restore.error ?? courtesy.error ?? removeVoucher.error)}</Alert>}
        {p.agreementText && (
          <button className="self-start text-sm font-semibold text-brand hover:text-brand-deep" onClick={() => setAgreement(true)}>
            {p.signedVia ? `Signed agreement${p.signedName ? ` (${p.signedName}, ${formatDateTime(p.signedAt)})` : ""}` : "Agreement"}
          </button>
        )}
        {abandon.error && <Alert>{errorMessage(abandon.error)}</Alert>}
      </div>
      {agreement && (
        <Dialog title={`${p.name}: signed agreement`} wide onClose={() => setAgreement(false)}>
          <AgreementText text={p.agreementText} />
          {p.signedVia && (
            <p className="mt-4 border-t border-mist pt-3 text-sm text-muted">
              Signed by typing “{p.signedName}” {p.signedVia === "desk" ? "at the front desk" : p.signedVia === "online" ? "online" : "from a link"} on {formatDateTime(p.signedAt)}.
            </p>
          )}
        </Dialog>
      )}
    </Panel>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

type SellOptions = { readers: { id: string; label: string }[]; testMode: boolean };
const useSellOptions = () => useQuery({ queryKey: ["plan-sell-options"], queryFn: () => get<SellOptions>("/api/plans/sell-options") });

/** Test mode only: pretend the card was tapped on the reader. */
function DevTap({ paymentId, onDone }: { paymentId: string; onDone: () => void }) {
  const options = useSellOptions();
  if (!options.data?.testMode) return null;
  return (
    <Button variant="quiet" onClick={() => void post(`/api/dev/payments/${paymentId}/tap`).then(onDone, onDone)}>
      Simulate tap (test mode)
    </Button>
  );
}

const METHOD_LABELS: Record<SellMethod, string> = { card_on_file: "Card on file", card: "New card", card_present: "Card reader", cash: "Cash" };

/** Pick a plan, have the client sign its agreement, take the first payment. */
function SellDialog({ clientId, clientName, onClose }: { clientId: string; clientName: string; onClose: () => void }) {
  const plans = usePlans();
  const catalog = useCatalog();
  const nameOf = useNameOf(catalog.data);
  const cards = useQuery({ queryKey: ["client-cards", clientId], queryFn: () => get<SavedCardView[]>(`/api/clients/${clientId}/cards`) });
  const options = useSellOptions();
  const [planId, setPlanId] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [method, setMethod] = useState<SellMethod | null>(null);
  const [cardId, setCardId] = useState("");
  const [readerId, setReaderId] = useState("");
  const [cash, setCash] = useState<number | null>(null);
  const [result, setResult] = useState<SellResult | null>(null);

  const forSale = (plans.data ?? []).filter((p) => p.status === "active");
  const plan = forSale.find((p) => p.id === planId);
  const cardList = cards.data ?? [];
  const readerList = options.data?.readers ?? [];
  const methods: SellMethod[] = !plan
    ? []
    : plan.kind === "membership"
      ? [...(cardList.length ? (["card_on_file"] as const) : []), "card"]
      : [...(cardList.length ? (["card_on_file"] as const) : []), "card", ...(readerList.length ? (["card_present"] as const) : []), "cash"];
  const m = method && methods.includes(method) ? method : (methods[0] ?? null);
  const card = cardId || cardList[0]?.id || "";
  const reader = readerId || readerList[0]?.id || "";
  const signed = !plan?.agreementText || (agreed && signedName.trim().length >= 2);

  const sell = useMutation({
    mutationFn: () =>
      post<SellResult>(`/api/clients/${clientId}/plans`, {
        planId,
        method: m,
        savedCardId: m === "card_on_file" ? card : null,
        readerId: m === "card_present" ? reader : null,
        cashReceivedCents: m === "cash" ? cash : null,
        signedName: plan?.agreementText ? signedName.trim() : "",
        agreed: plan?.agreementText ? agreed : false,
      }),
    onSuccess: (r) => setResult(r),
  });
  const sold = result ? result.plans.find((p) => p.planId === planId) : undefined;
  const done = sold?.status === "active";

  if (plans.isLoading || cards.isLoading) return null;
  return (
    <Dialog title={done ? "Sold" : plan ? `${plan.name} for ${clientName}` : "Sell a membership or package"} wide onClose={onClose}>
      {done ? (
        <div className="flex flex-col gap-3">
          <Alert tone="ok">
            {sold.name} is active. {sold.vouchersLeft ? `${sold.vouchersLeft} voucher${sold.vouchersLeft === 1 ? "" : "s"} ready to use.` : ""}
            {result?.changeCents ? ` Change: ${formatCents(result.changeCents)}.` : ""}
          </Alert>
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : result?.session ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-soft">The client enters their card{plan?.kind === "membership" ? "; it's saved for monthly billing" : ""}.</p>
          <StripeCheckout session={result.session} onComplete={async () => setResult({ ...result, session: null, plans: await get<ClientPlanView[]>(`/api/clients/${clientId}/plans`) })} />
        </div>
      ) : result?.message ? (
        <ReaderWait clientId={clientId} message={result.message} onPaid={(plans) => setResult({ ...result, message: null, plans })} onClose={onClose} />
      ) : !plan ? (
        <div className="flex flex-col gap-2">
          {forSale.length === 0 && <Alert tone="info">No memberships or packages yet (Catalog → Memberships / Packages).</Alert>}
          {forSale.map((p) => (
            <button key={p.id} type="button" className="rounded-[var(--radius-control)] border border-line p-4 text-left hover:border-brand" onClick={() => setPlanId(p.id)}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-semibold">{p.name}</span>
                <span className="tabular-nums">{priceLabel(p)}</span>
              </div>
              <ul className="mt-1 text-sm text-ink-soft">
                {p.benefits.map((b) => (
                  <li key={b.id}>• {describeBenefit(b, p.kind, nameOf)}</li>
                ))}
              </ul>
              {p.kind === "membership" && p.minimumMonths > 0 && <p className="mt-1 text-xs text-muted">{p.minimumMonths}-month minimum</p>}
            </button>
          ))}
        </div>
      ) : (
        <SellForm
          plan={plan}
          clientName={clientName}
          signature={{ agreed, setAgreed, signedName, setSignedName }}
          payment={{ methods, method: m, setMethod, cards: cardList, card, setCardId, readers: readerList, reader, setReaderId, cash, setCash }}
          error={sell.error ? errorMessage(sell.error) : null}
          busy={sell.isPending}
          canSubmit={signed && !!m}
          onBack={() => (setPlanId(""), sell.reset())}
          onSubmit={() => sell.mutate()}
        />
      )}
    </Dialog>
  );
}

function SellForm({
  plan,
  clientName,
  signature: s,
  payment: pay,
  error,
  busy,
  canSubmit,
  onBack,
  onSubmit,
}: {
  plan: Plan;
  clientName: string;
  signature: { agreed: boolean; setAgreed: (v: boolean) => void; signedName: string; setSignedName: (v: string) => void };
  payment: {
    methods: SellMethod[];
    method: SellMethod | null;
    setMethod: (m: SellMethod) => void;
    cards: SavedCardView[];
    card: string;
    setCardId: (id: string) => void;
    readers: { id: string; label: string }[];
    reader: string;
    setReaderId: (id: string) => void;
    cash: number | null;
    setCash: (c: number | null) => void;
  };
  error: string | null;
  busy: boolean;
  canSubmit: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      {plan.agreementText && (
        <section className="flex flex-col gap-3">
          <p className="text-sm font-semibold">Hand the screen to {clientName} to read and sign.</p>
          <div className="max-h-72 overflow-y-auto rounded-[var(--radius-control)] border border-line p-4">
            <AgreementText text={plan.agreementText} />
          </div>
          <Checkbox label="I've read and agree to this agreement" checked={s.agreed} onChange={s.setAgreed} />
          <Field label="Type your full name to sign" autoComplete="off" value={s.signedName} onChange={(e) => s.setSignedName(e.target.value)} />
        </section>
      )}
      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h3 className="font-semibold">{plan.kind === "membership" ? "First month" : "Payment"}</h3>
          <span className="text-xl font-semibold tabular-nums">{formatCents(plan.priceCents)}</span>
        </div>
        {plan.kind === "membership" && <p className="text-sm text-muted">Then {formatCents(plan.priceCents)} on the same day each month, to this card.</p>}
        <div className="flex flex-wrap gap-2">
          {pay.methods.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => pay.setMethod(k)}
              className={`min-h-11 rounded-full border px-4 py-2 text-sm font-medium ${pay.method === k ? "border-brand bg-brand text-white" : "border-line bg-white hover:border-ink-soft"}`}
            >
              {METHOD_LABELS[k]}
            </button>
          ))}
        </div>
        {pay.method === "card_on_file" && (
          <SelectField label="Card" value={pay.card} onChange={(e) => pay.setCardId(e.target.value)}>
            {pay.cards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.brand.toUpperCase()} •••• {c.last4}
                {c.expMonth ? ` (exp ${c.expMonth}/${String(c.expYear).slice(-2)})` : ""}
              </option>
            ))}
          </SelectField>
        )}
        {pay.method === "card_present" && (
          <SelectField label="Reader" value={pay.reader} onChange={(e) => pay.setReaderId(e.target.value)}>
            {pay.readers.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </SelectField>
        )}
        {pay.method === "cash" && <MoneyField label="Cash received (optional)" optional cents={pay.cash} onChange={pay.setCash} hint="For working out change." />}
      </section>
      {error && <Alert>{error}</Alert>}
      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="quiet" onClick={onBack}>
          ← Other plans
        </Button>
        <Button variant="primary" busy={busy} disabled={!canSubmit} onClick={onSubmit}>
          {pay.method === "card" ? "Enter a new card" : pay.method === "card_present" ? "Send to reader" : `Charge ${formatCents(plan.priceCents)}`}
        </Button>
      </div>
    </div>
  );
}

/** Waiting on the card reader: check until the plan is paid. */
function ReaderWait({ clientId, message, onPaid, onClose }: { clientId: string; message: string; onPaid: (p: ClientPlanView[]) => void; onClose: () => void }) {
  const poll = useQuery({ queryKey: ["client-plans-poll", clientId], queryFn: () => get<ClientPlanView[]>(`/api/clients/${clientId}/plans`), refetchInterval: 2000 });
  const pending = poll.data?.find((p) => p.status === "pending");
  const paid = !!poll.data && !pending;
  useEffect(() => {
    if (paid && poll.data) onPaid(poll.data);
  }, [paid, poll.data, onPaid]);
  return (
    <div className="flex flex-col gap-3">
      <Alert tone="info">{message}</Alert>
      {pending?.pendingPaymentId && <DevTap paymentId={pending.pendingPaymentId} onDone={() => void poll.refetch()} />}
      <div className="flex justify-end">
        <Button variant="quiet" onClick={onClose}>
          Close (it'll finish when the card is tapped)
        </Button>
      </div>
    </div>
  );
}

/** Cancel (with the end date shown first), take payment for a held month, change the card, or end it now. */
function MembershipActions({ p, clientId, onChanged }: { p: ClientPlanView; clientId: string; onChanged: () => void }) {
  const { can } = useSession();
  const cards = useQuery({ queryKey: ["client-cards", clientId], queryFn: () => get<SavedCardView[]>(`/api/clients/${clientId}/cards`) });
  const [cancelling, setCancelling] = useState(false);
  const [cardId, setCardId] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const preview = useQuery({ queryKey: ["cancel-preview", p.id], queryFn: () => get<{ endsOn: string; inCommitment: boolean; noticeDays: number; commitmentEndsOn: string | null }>(`/api/client-plans/${p.id}/cancel-preview`), enabled: cancelling });
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: (r) => {
      const m = (r as { message?: string | null; link?: string; cardLink?: boolean; sent?: boolean } | undefined) ?? {};
      setNote(
        m.cardLink
          ? m.sent
            ? "Card link texted. Once the client adds a card, it's billed for this membership."
            : "The text didn't go out (check the client's mobile number and texting consent)."
          : m.link
            ? "Pay link sent to the client."
            : m.message
              ? `Not paid: ${m.message}`
              : null,
      );
      setCancelling(false);
      onChanged();
    },
  });
  const list = cards.data ?? [];
  const chosen = cardId || list[0]?.id || "";
  const sell = can("plans.sell");
  const failed = p.charges.find((c) => c.status === "failed");
  return (
    <div className="flex flex-col gap-3">
      {p.status === "on_hold" && failed && (
        <Alert>
          The {fmtDate(failed.periodStart)} payment of {formatCents(failed.amountCents)} didn't go through{failed.failureMessage ? ` (${failed.failureMessage})` : ""}. Vouchers and member pricing are paused until it's paid.
        </Alert>
      )}
      {note && <Alert tone="info">{note}</Alert>}
      {sell && (
        <div className="flex flex-wrap items-end gap-2">
          {list.length > 0 && (
            <SelectField label="Card" className="min-w-56" value={chosen} onChange={(e) => setCardId(e.target.value)}>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.brand.toUpperCase()} •••• {c.last4}
                </option>
              ))}
            </SelectField>
          )}
          {p.status === "on_hold" && list.length > 0 && (
            <Button variant="primary" busy={act.isPending} onClick={() => act.mutate(() => post(`/api/client-plans/${p.id}/retry`, { savedCardId: chosen }))}>
              Charge {formatCents(failed?.amountCents ?? p.priceCents)}
            </Button>
          )}
          {p.status === "on_hold" && (
            <Button busy={act.isPending} onClick={() => act.mutate(() => post(`/api/client-plans/${p.id}/pay-link`))}>
              Text a pay link
            </Button>
          )}
          {!p.card && (
            <Button busy={act.isPending} onClick={() => act.mutate(() => post(`/api/clients/${clientId}/card-link`, { channel: "sms", clientPlanId: p.id }).then((r) => ({ message: null, sent: (r as { sent: boolean }).sent, cardLink: true })))}>
              Text a link to add a card
            </Button>
          )}
          {list.length > 0 && chosen && (!p.card || list.find((c) => c.id === chosen)?.last4 !== p.card.last4) && (
            <Button variant="quiet" busy={act.isPending} onClick={() => act.mutate(() => post(`/api/client-plans/${p.id}/card`, { savedCardId: chosen }))}>
              Bill this card monthly
            </Button>
          )}
          <span className="flex-1" />
          {p.cancelEffectiveOn ? (
            <Button variant="quiet" busy={act.isPending} onClick={() => act.mutate(() => post(`/api/client-plans/${p.id}/uncancel`))}>
              Undo cancellation
            </Button>
          ) : (
            <Button variant="quiet" onClick={() => setCancelling(true)}>
              Cancel membership…
            </Button>
          )}
          {can("plans.manage_client") && (
            <Button
              variant="quiet"
              onClick={() => {
                const reason = prompt("End it now? Unused vouchers are forfeited and nothing more is charged. Reason:");
                if (reason) act.mutate(() => post(`/api/client-plans/${p.id}/end`, { reason }));
              }}
            >
              End now…
            </Button>
          )}
        </div>
      )}
      {act.error && <Alert>{errorMessage(act.error)}</Alert>}
      {cancelling && (
        <Dialog
          title={`Cancel ${p.name}`}
          onClose={() => setCancelling(false)}
          footer={
            <>
              <Button variant="quiet" onClick={() => setCancelling(false)}>
                Keep it
              </Button>
              <Button variant="danger" disabled={!preview.data} busy={act.isPending} onClick={() => act.mutate(() => post(`/api/client-plans/${p.id}/cancel`))}>
                Cancel membership
              </Button>
            </>
          }
        >
          {preview.isLoading ? (
            <Loading />
          ) : preview.data ? (
            <div className="flex flex-col gap-2 text-sm">
              <p>
                It ends on <strong>{fmtDate(preview.data.endsOn)}</strong>, with no charge from then on. Until then it's billed and used as usual.
              </p>
              {preview.data.inCommitment && <p className="text-ink-soft">The minimum commitment runs to {fmtDate(preview.data.commitmentEndsOn)}.</p>}
              {preview.data.noticeDays > 0 && <p className="text-ink-soft">It needs {preview.data.noticeDays} days' notice before a billing date.</p>}
              <p className="text-ink-soft">A manager can end it sooner (for example, a documented medical reason) with End now.</p>
            </div>
          ) : (
            <Alert>{errorMessage(preview.error)}</Alert>
          )}
        </Dialog>
      )}
    </div>
  );
}

/**
 * Managers: a membership the client already paid for elsewhere this month (e.g. Boulevard). Nothing is
 * charged now; billing starts on the date picked. Month to month, no new agreement, no vouchers.
 */
function BringOverDialog({ clientId, clientName, onClose }: { clientId: string; clientName: string; onClose: () => void }) {
  const plans = usePlans();
  const cards = useQuery({ queryKey: ["client-cards", clientId], queryFn: () => get<SavedCardView[]>(`/api/clients/${clientId}/cards`) });
  const memberships = (plans.data ?? []).filter((p) => p.status === "active" && p.kind === "membership");
  const [planId, setPlanId] = useState("");
  const [nextBillingOn, setNextBillingOn] = useState("");
  const [cardId, setCardId] = useState<string | null>(null);
  const card = cardId ?? cards.data?.[0]?.id ?? "";
  const plan = memberships.find((p) => p.id === planId);
  const save = useMutation({
    mutationFn: () => post<ClientPlanView[]>(`/api/clients/${clientId}/plans/transfer`, { planId, nextBillingOn, savedCardId: card || null }),
    onSuccess: onClose,
  });
  if (plans.isLoading || cards.isLoading) return null;
  return (
    <Dialog
      title={`Bring over a membership for ${clientName}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!plan || !nextBillingOn} busy={save.isPending} onClick={() => save.mutate()}>
            Bring it over
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-soft">
          For a member who already paid this month somewhere else (e.g. Boulevard). It's active now, nothing is charged until the next billing date, and it's month to month with no new agreement. It starts with no vouchers: add any they still have with “+ Add a voucher”.
        </p>
        <SelectField label="Membership" value={planId} onChange={(e) => setPlanId(e.target.value)}>
          <option value="">Pick one…</option>
          {memberships.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} · {priceLabel(p)}
            </option>
          ))}
        </SelectField>
        <Field label="Next billing date" type="date" value={nextBillingOn} onChange={(e) => setNextBillingOn(e.target.value)} hint={plan ? `${formatCents(plan.priceCents)} is charged on this date, then monthly.` : undefined} />
        <SelectField label="Card to bill" value={card} onChange={(e) => setCardId(e.target.value)}>
          {(cards.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.brand.toUpperCase()} •••• {c.last4}
            </option>
          ))}
          <option value="">None yet (text them a link to add one)</option>
        </SelectField>
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      </div>
    </Dialog>
  );
}

/** Text or email a client a link to add a card on file (nothing charged). */
export function CardLinkButtons({ clientId }: { clientId: string }) {
  const [note, setNote] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: (channel: "sms" | "email") => post<{ link: string; sent: boolean }>(`/api/clients/${clientId}/card-link`, { channel }),
    onSuccess: (r, channel) => setNote(r.sent ? `Link ${channel === "sms" ? "texted" : "emailed"}. It works for 7 days.` : `It didn't go out (check the client's ${channel === "sms" ? "mobile number and texting consent" : "email"}).`),
  });
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <Button busy={send.isPending && send.variables === "sms"} onClick={() => send.mutate("sms")}>
          Text a link to add a card
        </Button>
        <Button variant="quiet" busy={send.isPending && send.variables === "email"} onClick={() => send.mutate("email")}>
          Email it
        </Button>
      </div>
      {note && <Alert tone="info">{note}</Alert>}
      {send.error && <Alert>{errorMessage(send.error)}</Alert>}
    </div>
  );
}
